import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CalendarDays, FileJson, History, RotateCcw, ShieldCheck, Upload } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { usePatients } from "@/lib/usePatients";
import { parseImportFile, sha256Text } from "@/lib/importers/parsers";
import { persistImportPreview, revertImportBatch, type PersistImportResult } from "@/lib/importers/persistence";
import type { ImportPreview, ImportVisibility } from "@/lib/importers/contracts";
import { toast } from "@/lib/safeToast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";

const PATIENT_STORAGE_KEY = "clinicalos-my-space-patient";

function fmt(value: string | null | undefined) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(new Date(value));
}

function eventLabel(event: any) {
  const dose = event.doseAmount != null ? `${event.doseAmount}${event.doseUnit ? ` ${event.doseUnit}` : ""}` : null;
  return [event.rawName, dose, event.note].filter(Boolean).join(" · ") || event.eventType;
}

export function JournalImportPanel() {
  const { user } = useAuth();
  const { data: patients = [] } = usePatients();
  const queryClient = useQueryClient();
  const [patientId, setPatientId] = useState(() => localStorage.getItem(PATIENT_STORAGE_KEY) ?? "");
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [fileSha256, setFileSha256] = useState("");
  const [fileName, setFileName] = useState("");
  const [includeDemonstrations, setIncludeDemonstrations] = useState(false);
  const [visibilityScope, setVisibilityScope] = useState<ImportVisibility>("personal");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState<PersistImportResult | null>(null);

  const { data: batches = [], refetch: refetchBatches } = useQuery({
    queryKey: ["clinical-import-batches", patientId],
    enabled: !!user?.id && !!patientId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("clinical_import_batches")
        .select("*")
        .eq("owner_id", user!.id)
        .eq("patient_id", patientId)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return data ?? [];
    },
  });

  const visibleEvents = useMemo(() => preview?.events.filter((event) => includeDemonstrations || !event.excludedByDefault) ?? [], [preview, includeDemonstrations]);

  function selectPatient(value: string) {
    setPatientId(value);
    if (value) localStorage.setItem(PATIENT_STORAGE_KEY, value);
  }

  async function readFile(file?: File | null) {
    if (!file) return;
    setLastResult(null);
    setBusy(true);
    try {
      const text = await file.text();
      const parsed = parseImportFile(text, file.name);
      const hash = await sha256Text(text);
      setFileName(file.name);
      setFileSha256(hash);
      setPreview(parsed);
      toast.success(`${parsed.summary.total} registros analisados localmente.`);
    } catch (error: any) {
      setPreview(null);
      setFileSha256("");
      toast.error(error?.message ?? "Não foi possível interpretar o arquivo.");
    } finally {
      setBusy(false);
    }
  }

  async function importNow() {
    if (!user || !preview || !patientId || !fileSha256) return;
    const patient = patients.find((item: any) => item.id === patientId);
    const count = visibleEvents.length;
    if (!confirm(`Importar ${count} eventos de ${fileName} para ${patient?.full_name ?? "o perfil selecionado"}? O lote poderá ser revertido.`)) return;
    setBusy(true);
    try {
      const result = await persistImportPreview({
        user,
        patientId,
        preview,
        fileSha256,
        visibilityScope,
        includeExcluded: includeDemonstrations,
      });
      setLastResult(result);
      if (result.duplicateFile) toast.warning("Este mesmo arquivo já possui um lote ativo para o perfil selecionado.");
      else toast.success(`${result.imported} eventos importados; ${result.mirrored} integrados à timeline por horário.`);
      await Promise.all([
        refetchBatches(),
        queryClient.invalidateQueries({ queryKey: ["psylog-events"] }),
        queryClient.invalidateQueries({ queryKey: ["clinical-import-events"] }),
        queryClient.invalidateQueries({ queryKey: ["my-space-imported-events"] }),
      ]);
    } catch (error: any) {
      console.error("[JournalImportPanel] import failed", error);
      toast.error(error?.message ?? "Falha ao importar o lote.");
    } finally {
      setBusy(false);
    }
  }

  async function revert(batch: any) {
    if (!user) return;
    if (!confirm(`Reverter o lote ${batch.source_file_name}? Os eventos importados serão ocultados, sem exclusão física.`)) return;
    setBusy(true);
    try {
      const result = await revertImportBatch(user, batch.id);
      toast.success(`${result.reverted} eventos revertidos com trilha de auditoria.`);
      await Promise.all([
        refetchBatches(),
        queryClient.invalidateQueries({ queryKey: ["psylog-events"] }),
        queryClient.invalidateQueries({ queryKey: ["clinical-import-events"] }),
        queryClient.invalidateQueries({ queryKey: ["my-space-imported-events"] }),
      ]);
    } catch (error: any) {
      toast.error(error?.message ?? "Falha ao reverter o lote.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <Card className="space-y-4 border-primary/20 p-4 md:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 font-semibold"><FileJson className="h-4 w-4 text-primary" />DrugsPro Journal e CSV de eventos</div>
            <p className="mt-1 max-w-3xl text-xs text-muted-foreground">O arquivo é interpretado no navegador. Eventos com apenas data permanecem sem horário e não entram artificialmente na curva PK/PD. Cada importação forma um lote reversível.</p>
          </div>
          <Badge variant="outline">JSON / CSV</Badge>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Perfil/paciente de destino">
            <select className="h-11 w-full rounded-md border border-input bg-background px-3 text-base sm:h-10 sm:text-sm" value={patientId} onChange={(event) => selectPatient(event.target.value)}>
              <option value="">Selecione…</option>
              {patients.map((patient: any) => <option key={patient.id} value={patient.id}>{patient.social_name || patient.full_name}</option>)}
            </select>
          </Field>
          <Field label="Visibilidade do lote">
            <select className="h-11 w-full rounded-md border border-input bg-background px-3 text-base sm:h-10 sm:text-sm" value={visibilityScope} onChange={(event) => setVisibilityScope(event.target.value as ImportVisibility)}>
              <option value="personal">Meu Espaço privado</option>
              <option value="clinical">Espaço Clínico</option>
              <option value="shared">Compartilhado entre os espaços</option>
            </select>
          </Field>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-muted">
            <Upload className="h-4 w-4" />Selecionar JSON ou CSV
            <input type="file" accept="application/json,.json,text/csv,.csv" className="hidden" onChange={(event) => readFile(event.target.files?.[0])} />
          </label>
          {fileName ? <span className="max-w-full truncate text-xs text-muted-foreground">{fileName}</span> : null}
        </div>

        {preview ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 lg:grid-cols-6">
              <Metric label="Total" value={preview.summary.total} />
              <Metric label="Importados padrão" value={preview.summary.includedByDefault} />
              <Metric label="Demonstrações" value={preview.summary.excludedDemonstrations} />
              <Metric label="Somente data" value={preview.summary.dateOnly} />
              <Metric label="Sem unidade" value={preview.summary.missingDoseUnit} />
              <Metric label="Alertas" value={preview.summary.warnings} />
            </div>

            {preview.summary.excludedDemonstrations ? (
              <label className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/5 p-3 text-sm">
                <input type="checkbox" className="mt-1" checked={includeDemonstrations} onChange={(event) => setIncludeDemonstrations(event.target.checked)} />
                <span><b>Incluir registros demonstrativos</b><br /><span className="text-xs text-muted-foreground">Desligado por padrão. Só marque quando confirmar que esses itens representam eventos reais.</span></span>
              </label>
            ) : null}

            {preview.summary.dateOnly ? (
              <div className="flex items-start gap-2 rounded-lg border border-info/30 bg-info/5 p-3 text-xs text-muted-foreground"><CalendarDays className="mt-0.5 h-4 w-4 shrink-0" />{preview.summary.dateOnly} evento(s) serão preservados por data, fora da curva intradiária até receberem horário confirmado.</div>
            ) : null}

            <details className="rounded-lg border">
              <summary className="cursor-pointer list-none p-3 text-sm font-medium">Prévia dos eventos ({visibleEvents.length})</summary>
              <div className="max-h-[440px] divide-y overflow-auto border-t">
                {visibleEvents.slice(0, 250).map((event, index) => (
                  <div key={`${event.sourceRecordId}-${index}`} className="space-y-1 p-3 text-xs">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <span className="font-medium">{eventLabel(event)}</span>
                      <div className="flex gap-1"><Badge variant="outline" className="text-[9px]">{event.eventType}</Badge><Badge variant="secondary" className="text-[9px]">{event.timePrecision}</Badge></div>
                    </div>
                    <div className="text-muted-foreground">{event.occurredAt ?? event.localDate ?? "data inválida"} · confiança {event.confidence}</div>
                    {event.warnings.length ? <div className="text-warning">{event.warnings.join(" ")}</div> : null}
                  </div>
                ))}
              </div>
            </details>

            <div className="flex flex-col gap-3 rounded-lg border bg-muted/20 p-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-xs text-muted-foreground"><ShieldCheck className="mr-1 inline h-4 w-4" />Hash SHA-256 impede importar novamente o mesmo arquivo para o mesmo perfil enquanto o lote estiver ativo.</div>
              <Button onClick={importNow} disabled={busy || !patientId}>{busy ? "Processando…" : `Importar ${visibleEvents.length} eventos`}</Button>
            </div>
          </div>
        ) : null}

        {lastResult ? (
          <div className="rounded-lg border border-success/30 bg-success/5 p-3 text-sm">
            {lastResult.duplicateFile ? "Arquivo já importado neste perfil." : `${lastResult.imported} importados · ${lastResult.mirrored} na timeline horária · ${lastResult.dateOnly} preservados apenas por data · ${lastResult.excluded} excluídos.`}
          </div>
        ) : null}
      </Card>

      <Card className="space-y-3 p-4 md:p-5">
        <div className="flex items-center gap-2 font-semibold"><History className="h-4 w-4" />Histórico de lotes</div>
        {!patientId ? <div className="text-sm text-muted-foreground">Selecione um perfil para consultar importações anteriores.</div> : !batches.length ? <div className="text-sm text-muted-foreground">Nenhum lote para este perfil.</div> : (
          <div className="space-y-2">
            {batches.map((batch: any) => (
              <div key={batch.id} className="flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{batch.source_file_name}</div>
                  <div className="text-xs text-muted-foreground">{batch.source_app} · {fmt(batch.created_at)} · {batch.imported_items}/{batch.total_items} importados · {batch.warning_count} alertas</div>
                  <div className="mt-1 flex flex-wrap gap-1"><Badge variant="outline" className="text-[9px]">{batch.status}</Badge><Badge variant="secondary" className="text-[9px]">{batch.visibility_scope}</Badge></div>
                </div>
                {batch.status !== "reverted" ? <Button variant="outline" size="sm" disabled={busy} onClick={() => revert(batch)}><RotateCcw className="mr-1.5 h-4 w-4" />Reverter lote</Button> : <Badge variant="outline">revertido {fmt(batch.reverted_at)}</Badge>}
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="flex items-start gap-2 text-xs text-muted-foreground"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />Importação preserva fonte e contexto. Não classifica automaticamente uso como prescrito, recaída, abuso ou causalidade clínica.</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="min-w-0 space-y-1.5"><Label className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</Label>{children}</div>;
}

function Metric({ label, value }: { label: string; value: number }) {
  return <div className="rounded-lg border p-3"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div><div className="mt-1 text-xl font-semibold tabular-nums">{value}</div></div>;
}
